/**
 * EIA Open Data API — natural-gas pricing by state (v1.2.1 HR-5 P2-4).
 *
 * Companion to `eiaElectricity.ts`. Resolves the state from coordinates
 * (FCC Census Block, free + keyless) then queries the EIA v2 natural-gas
 * price series. The EIA `api_key` is injected SERVER-SIDE by the proxy
 * (`/api/proxy` → `SERVER_API_KEYS['api.eia.gov']` → `EIA_API_KEY`); this
 * is the SAME key already deployed for `eia-electricity`, so the provider
 * needs NO new secret / host / proxy plumbing (`requiresKey: false`).
 *
 * Price resolution ladder (first usable wins):
 *   1. State CITYGATE price  (EIA process code PG2) — closest to a local
 *      delivered gas price; preferred.
 *   2. State INDUSTRIAL price (EIA process code PIN) — fallback when the
 *      state has no citygate series for the latest period.
 *   3. National HENRY HUB spot (RNGWHHD) — national benchmark fallback so
 *      a gas pathway is NEVER left ungrounded when at least HH is up.
 *
 * AUDIT HONESTY (CLAUDE.md rule 5): the `priceSeries` field records which
 * tier produced the price so downstream prose (`sedToText`) can state
 * explicitly whether the figure is a local state citygate or the national
 * Henry Hub benchmark — it must never present national HH as a local price.
 *
 * UNITS: citygate / industrial state prices are published in $/Mcf; Henry
 * Hub is published in $/MMBtu. We carry both $/Mcf and $/MMBtu, converting
 * Mcf → MMBtu with the named heating-value constant `BTU_PER_CF` (≈1037
 * Btu/cf ⇒ ~1.037 MMBtu/Mcf). Heating value varies by gas composition; the
 * pipeline `nearestPipelineGasComposition` (KMZ) could refine this HHV but
 * that is out of scope here (flagged, not implemented).
 */
import type { DataProvider, ProviderGeoContext, ProviderResult } from './types';
import { CACHE_TTL, fetchWithTimeout } from './types';
import { resolveFipsFromRefdata } from '../refdata/fipsLookup';

/**
 * Higher heating value of pipeline-quality natural gas, Btu per cubic foot.
 * EIA's standard conversion is 1,037 Btu/cf (≈1.037 MMBtu per Mcf). Named
 * constant — NOT a magic number — per CLAUDE.md unit-consistency rule.
 */
export const BTU_PER_CF = 1037;
/** MMBtu per Mcf (thousand cubic feet) = BTU_PER_CF × 1000 cf / 1e6 Btu. */
export const MMBTU_PER_MCF = (BTU_PER_CF * 1000) / 1_000_000; // ≈1.037

export interface NaturalGasData {
  /** State citygate / industrial price in $/Mcf (null when only HH was found). */
  citygatePriceUsdPerMcf: number | null;
  /** Resolved local price in $/MMBtu (citygate/industrial ÷ MMBTU_PER_MCF). */
  citygatePriceUsdPerMmbtu: number | null;
  /** National Henry Hub spot benchmark in $/MMBtu, when available. */
  henryHubSpotUsdPerMmbtu: number | null;
  /** State abbreviation resolved from coordinates (FCC block). */
  state: string;
  /** EIA reporting period of the resolved price (e.g. '2025-12' or '2026-01-15'). */
  period: string;
  /** Which series produced the resolved price — keeps the locality claim honest. */
  priceSeries: 'citygate' | 'industrial' | 'henry-hub-fallback';
}

/** Parse an EIA `value` field (string or number) into a finite number, else null. */
function parseEiaValue(raw: unknown): number | null {
  const n = typeof raw === 'string' ? parseFloat(raw) : (typeof raw === 'number' ? raw : NaN);
  return Number.isFinite(n) ? n : null;
}

/**
 * Fetch the latest record for one EIA v2 natural-gas state price series.
 * Returns `{ price, period }` ($/Mcf) or null when the series is empty /
 * the request fails (caller decides whether to fall through to the next
 * tier). Does NOT throw — tier fall-through is the resolution mechanism.
 */
async function fetchStatePrice(
  stateCode: string,
  process: 'PG2' | 'PIN',
  signal?: AbortSignal,
): Promise<{ price: number; period: string } | null> {
  // EIA v2 natural-gas price summary: duoarea S<ST> = the state, process
  // PG2 = citygate, PIN = industrial. Latest single period.
  const params = new URLSearchParams([
    ['frequency', 'monthly'],
    ['data[0]', 'value'],
    ['facets[duoarea][]', `S${stateCode}`],
    ['facets[process][]', process],
    ['sort[0][column]', 'period'],
    ['sort[0][direction]', 'desc'],
    ['length', '1'],
  ]);
  const url = `https://api.eia.gov/v2/natural-gas/pri/sum/data/?${params.toString()}`;
  try {
    const res = await fetchWithTimeout(url, 15_000, signal);
    if (!res.ok) return null;
    const json = await res.json();
    const record = json?.response?.data?.[0];
    if (!record) return null;
    const price = parseEiaValue(record.value);
    if (price === null || price <= 0) return null;
    return { price, period: String(record.period ?? '') };
  } catch {
    return null;
  }
}

/**
 * Fetch the latest national Henry Hub spot price ($/MMBtu). Returns null
 * when empty / failed. Does NOT throw.
 */
async function fetchHenryHub(signal?: AbortSignal): Promise<{ price: number; period: string } | null> {
  const params = new URLSearchParams([
    ['frequency', 'daily'],
    ['data[0]', 'value'],
    ['facets[series][]', 'RNGWHHD'],
    ['sort[0][column]', 'period'],
    ['sort[0][direction]', 'desc'],
    ['length', '1'],
  ]);
  const url = `https://api.eia.gov/v2/natural-gas/pri/fut/data/?${params.toString()}`;
  try {
    const res = await fetchWithTimeout(url, 15_000, signal);
    if (!res.ok) return null;
    const json = await res.json();
    const record = json?.response?.data?.[0];
    if (!record) return null;
    const price = parseEiaValue(record.value);
    if (price === null || price <= 0) return null;
    return { price, period: String(record.period ?? '') };
  } catch {
    return null;
  }
}

export const eiaNaturalGasProvider: DataProvider<NaturalGasData> = {
  id: 'eia-natural-gas',
  name: 'EIA Natural Gas Prices',
  cacheTtlMs: CACHE_TTL.STANDARD,
  requiresKey: false, // EIA_API_KEY injected server-side by proxy (same key as eia-electricity)

  async fetch(lat, lng, _apiKey?: string, geo?: ProviderGeoContext): Promise<ProviderResult<NaturalGasData>> {
    // Resolve state from coordinates — identical to eiaElectricity:
    // RD-1 (C5) local-first tiger-counties snapshot, FCC live fallback.
    let stateCode: string | undefined = (await resolveFipsFromRefdata(lat, lng))?.stateCode;
    if (!stateCode) {
      const fccRes = await fetchWithTimeout(
        `https://geo.fcc.gov/api/census/block/find?format=json&latitude=${lat}&longitude=${lng}`,
        15_000,
        geo?.signal,
      );
      if (!fccRes.ok) throw new Error('FCC geocoding failed');
      const fccJson = await fccRes.json();
      stateCode = fccJson?.State?.code;
    }
    if (!stateCode) throw new Error('Could not determine state from coordinates');

    // Always fetch Henry Hub in parallel — it's the national benchmark we
    // surface alongside the local price AND the final fallback.
    const [citygate, henryHub] = await Promise.all([
      fetchStatePrice(stateCode, 'PG2', geo?.signal),
      fetchHenryHub(geo?.signal),
    ]);

    let series: NaturalGasData['priceSeries'];
    let localPerMcf: number | null = null;
    let localPerMmbtu: number | null = null;
    let period: string;

    if (citygate) {
      series = 'citygate';
      localPerMcf = citygate.price;
      localPerMmbtu = citygate.price / MMBTU_PER_MCF;
      period = citygate.period;
    } else {
      // Citygate empty — try industrial state price before falling to HH.
      const industrial = await fetchStatePrice(stateCode, 'PIN', geo?.signal);
      if (industrial) {
        series = 'industrial';
        localPerMcf = industrial.price;
        localPerMmbtu = industrial.price / MMBTU_PER_MCF;
        period = industrial.period;
      } else if (henryHub) {
        series = 'henry-hub-fallback';
        // HH is already $/MMBtu; no local $/Mcf figure for this tier.
        localPerMmbtu = henryHub.price;
        period = henryHub.period;
      } else {
        // No usable price from any tier — mirror eiaElectricity's throw so
        // the orchestrator records the section unavailable honestly.
        throw new Error('EIA returned no natural-gas price for this state or Henry Hub');
      }
    }

    const henryHubPerMmbtu = henryHub?.price ?? null;

    const seriesLabel =
      series === 'citygate'
        ? `${stateCode} citygate`
        : series === 'industrial'
          ? `${stateCode} industrial`
          : 'Henry Hub (national benchmark)';

    const priceForSource = localPerMmbtu ?? henryHubPerMmbtu;

    return {
      data: {
        citygatePriceUsdPerMcf: localPerMcf,
        citygatePriceUsdPerMmbtu: localPerMmbtu,
        henryHubSpotUsdPerMmbtu: henryHubPerMmbtu,
        state: stateCode,
        period,
        priceSeries: series,
      },
      sources: [
        {
          field: 'naturalGas',
          source:
            `EIA Natural Gas — ${seriesLabel}: ` +
            `${priceForSource != null ? `$${priceForSource.toFixed(2)}/MMBtu` : 'price unavailable'} (${period})`,
          url: 'https://www.eia.gov/naturalgas/data.php',
          retrievedAt: new Date().toISOString(),
        },
      ],
    };
  },
};
